import {
	number as validateNumber,
	string as validateString,
	ArraySchema,
	ObjectSchema,
	AnySchema,
} from "yup"
import pluralize from "pluralize"
import { parsePhoneNumber as parsePhoneNumberInput } from "react-phone-number-input/input"

/**
 * Check for a file size (KB)
 */
export function checkFileSize(max: number) {
	return function checkFilesForSize(files?: unknown | File | [File]): boolean {
		let valid = true
		if (files) {
			if (!Array.isArray(files)) {
				files = [files]
			}
			if (Array.isArray(files)) {
				files.every((file) => {
					const size = file.size / 1024
					if (size > max) {
						valid = false
					}
					return valid
				})
			}
		}
		return valid
	}
}

export function checkFileTypes(types: Array<string>) {
	return function checkTypesForFiles(files?: unknown | File | [File]): boolean {
		let valid = true
		if (files) {
			if (!Array.isArray(files)) {
				files = [files]
			}
			if (Array.isArray(files)) {
				files.every((file) => {
					if (!types.includes(file.type)) {
						valid = false
					}
					return valid
				})
			}
		}
		return valid
	}
}

/**
 * Helper to validate the numbers that can be empty string
 */
export function EmptyNumberValidator(message = "Please enter a valid number") {
	return validateNumber()
		.transform(function (value, originalValue) {
			if (this.isType(value)) return value
			if (!originalValue || !originalValue.trim()) {
				return null
			}
			return originalValue
		})
		.nullable()
		.typeError(message)
}

export function PincodeValidator(label = "Pincode Number") {
	return validateString().typeError(`${label} should be a numeric value`)
}

export function DigitsValidator(digits: number, label = "Field Value") {
	return EmptyNumberValidator(`${label} should be a numeric value`)
		.typeError(`${label} should be a numeric value`)
		.test({
			name: `${digits}-digits-${label}`,
			test: (value: number | string | null | undefined) => {
				const currentLength = value ? String(value).trim().length : 0
				if (currentLength === 0) {
					// required attribute should handle the empty case
					return true
				}
				return currentLength === digits
			},
			message: function test(params) {
				const currentLength = params.value
					? String(params.value).trim().length
					: 0
				return `${label} must of ${digits} ${pluralize("digit", digits)}${
					currentLength > 0
						? ` (entered ${currentLength} ${pluralize("digit", currentLength)})`
						: ""
				}`
			},
		})
}

export function getEditablePhoneNumber(
	phone_number: string,
	defaultCountryCode = "IN"
) {
	try {
		const n = parsePhoneNumberInput(phone_number)
		if (!n) throw new Error(`Can not parse phone number ${phone_number}`)
		return {
			number: n.nationalNumber,
			phone_number: n.number.toString(),
			country_code: n.country ? n.country.toString() : defaultCountryCode,
		}
	} catch (e) {
		const error = e as Error
		console.warn(
			error.message ||
				`Something went wrong while parsing the phone number ${phone_number}`
		)
		return {
			number: phone_number,
			phone_number: phone_number,
			country_code: defaultCountryCode || "IN",
		}
	}
}

export function getSchemaOfField<Type>(
	field: string,
	parentSchema: AnySchema<Type>
): AnySchema<Type> | undefined {
	// Convert `items[0][0].name` → ["items", "0", "0", "name"]
	const parts = field.replace(/\[(\d+)\]/g, ".$1").split(".")

	let current: AnySchema<Type> | undefined = parentSchema

	for (const part of parts) {
		if (!current) return undefined

		if (current instanceof ObjectSchema) {
			current = (current as ObjectSchema<never>).fields[part]
		} else if (current instanceof ArraySchema) {
			// arrays must be accessed with a number
			if (!/^\d+$/.test(part)) {
				return undefined // invalid array access
			}
			// move to the inner part of the schema, skip the current part (index)
			current = (current as ArraySchema<Type[], unknown>).innerType as
				| AnySchema<Type>
				| undefined
		} else {
			return undefined
		}
	}

	return current
}

export function isFieldOfTypeArraySchema<Type>(
	field: string,
	parentSchema: AnySchema<Type>
): boolean {
	const fieldSchema = getSchemaOfField(field, parentSchema)
	if (fieldSchema instanceof ArraySchema) {
		return true
	}
	return false
}

// Unique sentinel — never matches a real field value
const VALIDATOR_UNSET = Symbol("unset")

/**
 * Mutable state bag shared between createAsyncFieldValidator and useAsyncFieldValidator.
 */
export type TAsyncValidatorState = {
	fn: (value: unknown, allValues: object) => Promise<string | undefined>
	wait: number
	timer: ReturnType<typeof setTimeout> | undefined
	lastValue: unknown
	lastResolve: ((result: string | undefined) => void) | undefined
	lastPromise: Promise<string | undefined> | undefined
	lastResult: string | undefined
	isResolved: boolean
}

/**
 * Core debounce/cache logic. Operates entirely on the mutable state object —
 * no closures over external variables. Called by both createAsyncFieldValidator
 * (closure-based state) and useAsyncFieldValidator (ref-based state).
 */
export function runAsyncValidation(
	s: TAsyncValidatorState,
	value: unknown,
	allValues: object
): string | undefined | Promise<string | undefined> {
	if (value === s.lastValue) {
		// Value unchanged — no timer reset, no new Promise
		if (s.isResolved) return s.lastResult // sync: isPromise()=false → field.validating never set
		if (s.lastPromise !== undefined) return s.lastPromise // debounce pending: reuse same Promise
	}

	// Value changed — resolve the old Promise immediately so react-final-form
	// stops waiting for it (abandoned Promises block form submission forever)
	if (s.lastResolve) {
		s.lastResolve(undefined)
		s.lastResolve = undefined
	}

	s.lastValue = value

	// Empty value — return synchronously so field.validating is never set
	// (avoids the "Checking..." flash on initial mount / field clear)
	if (value === "" || value === null || value === undefined) {
		s.isResolved = true
		s.lastResult = undefined
		s.lastPromise = undefined
		clearTimeout(s.timer)
		return undefined
	}

	// Start a fresh debounce cycle
	s.isResolved = false
	s.lastResult = undefined
	clearTimeout(s.timer)

	s.lastPromise = new Promise<string | undefined>((resolve) => {
		s.lastResolve = resolve
		s.timer = setTimeout(() => {
			s.lastResolve = undefined
			s.fn(value, allValues)
				.then((result) => {
					s.isResolved = true
					s.lastResult = result
					resolve(result)
				})
				.catch(() => {
					s.isResolved = true
					s.lastResult = undefined
					resolve(undefined)
				})
		}, s.wait)
	})

	return s.lastPromise
}

/**
 * Creates a debounced async field validator compatible with react-final-form's
 * field-level `validate` prop. The returned function holds debounce state in a
 * closure — keep the reference stable (via `useAsyncFieldValidator` or `useMemo`)
 * so the timer persists across renders.
 */
export function createAsyncFieldValidator<V>(
	fn: (value: V, allValues: object) => Promise<string | undefined>,
	{ wait = 300 }: { wait?: number } = {}
): (
	value: V,
	allValues: object
) => string | undefined | Promise<string | undefined> {
	const state: TAsyncValidatorState = {
		fn: fn as TAsyncValidatorState["fn"],
		wait,
		timer: undefined,
		lastValue: VALIDATOR_UNSET,
		lastResolve: undefined,
		lastPromise: undefined,
		lastResult: undefined,
		isResolved: false,
	}
	return (value: V, allValues: object) =>
		runAsyncValidation(state, value, allValues)
}
